import { addClass, removeClass, hasClass, toggleClass } from './util.js';
import './profile/validators.js';
import './profile/avatar-upload.js';
import './profile/password-change.js';
import './profile/profile-form.js';
import './profile/profile.js';
import './auth/password-toggle.js';
import './products/filter.js';

const HOME_DIAGNOSTIC_LIMIT = 30;

function recordHomeDiagnostic(event, details = {}) {
    if (typeof window === 'undefined') return;
    const diagnostics = window.__homeDiagnostics || [];
    const visualViewport = window.visualViewport ? {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
        offsetTop: window.visualViewport.offsetTop,
        scale: window.visualViewport.scale
    } : null;

    diagnostics.push(Object.assign({
        event,
        path: window.location ? window.location.pathname : '',
        standalone: !!(window.navigator && window.navigator.standalone),
        displayModeStandalone: !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches),
        visibilityState: document.visibilityState,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        visualViewport,
        ts: Date.now()
    }, details));

    window.__homeDiagnostics = diagnostics.slice(-HOME_DIAGNOSTIC_LIMIT);
}

function syncHomeViewportHeight() {
    if (!document.documentElement) return;
    const height = window.visualViewport && window.visualViewport.height
        ? window.visualViewport.height
        : window.innerHeight;
    if (height > 0) {
        document.documentElement.style.setProperty('--app-viewport-height', `${height}px`);
    }
}

function ensureHomeShellVisible(reason = 'check') {
    const body = document.body;
    if (!body || body.getAttribute('data-home-shell') !== 'true') return;

    syncHomeViewportHeight();

    const main = document.querySelector('main');
    const hero = document.getElementById('hero');
    const target = hero || main;
    const rect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;
    const bodyRect = body.getBoundingClientRect ? body.getBoundingClientRect() : null;
    const visible = !!(target && rect && rect.width > 0 && rect.height > 0 && bodyRect && bodyRect.height > 0);

    recordHomeDiagnostic('home-shell-visible-check', {
        reason,
        hasMain: !!main,
        hasHero: !!hero,
        targetWidth: rect ? rect.width : 0,
        targetHeight: rect ? rect.height : 0,
        bodyHeight: bodyRect ? bodyRect.height : 0,
        visible
    });

    if (!visible) {
        body.classList.add('home-boot-fallback');
        if (main) {
            main.style.minHeight = 'var(--app-viewport-height)';
        }
    }
}

function installHomeShellGuard() {
    if (typeof window === 'undefined' || !document.body || document.body.getAttribute('data-home-shell') !== 'true') return;

    ensureHomeShellVisible('init');

    window.addEventListener('pageshow', function () {
        ensureHomeShellVisible('pageshow');
    }, true);
    window.addEventListener('focus', function () {
        ensureHomeShellVisible('focus');
    }, true);
    window.addEventListener('orientationchange', function () {
        setTimeout(function () {
            ensureHomeShellVisible('orientationchange');
        }, 50);
    }, true);
    window.addEventListener('resize', function () {
        ensureHomeShellVisible('resize');
    }, true);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            ensureHomeShellVisible('visibilitychange');
        }
    }, true);

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', function () {
            ensureHomeShellVisible('visualViewport.resize');
        }, true);
        window.visualViewport.addEventListener('scroll', function () {
            ensureHomeShellVisible('visualViewport.scroll');
        }, true);
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('error', function (event) {
        recordHomeDiagnostic('window.error', {
            message: event.message || '',
            source: event.filename || '',
            line: event.lineno || 0,
            column: event.colno || 0
        });
    }, true);
    window.addEventListener('unhandledrejection', function (event) {
        const reason = event.reason || {};
        recordHomeDiagnostic('window.unhandledrejection', {
            message: reason.message || String(reason)
        });
    }, true);
}

// Initialize dropdown navigation for sb-has-children
function initDropdownNavigation() {
    var dropdownItems = document.querySelectorAll('.sb-has-children');

    for (var i = 0; i < dropdownItems.length; i++) {
        (function (item) {
            var link = item.querySelector('a');
            var submenu = item.querySelector('ul');
            if (!link || !submenu) return;
            // Prevent default link behavior
            link.addEventListener('click', function (e) {
                e.preventDefault();

                // Toggle active class
                var isActive = hasClass(item, 'active');

                // Close all other dropdowns
                var allDropdowns = document.querySelectorAll('.sb-has-children');
                for (var j = 0; j < allDropdowns.length; j++) {
                    removeClass(allDropdowns[j], 'active');
                }

                // Toggle current dropdown
                if (!isActive) {
                    addClass(item, 'active');
                }
            });

            // Close dropdown when clicking outside
            document.addEventListener('click', function (e) {
                if (!item.contains(e.target)) {
                    removeClass(item, 'active');
                }
            });
        })(dropdownItems[i]);
    }
}

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', function () {
    installHomeShellGuard();
    initializeFAQ();
    initDropdownNavigation();

    /** Newsletter subscribe (footer) — reuse existing fetch + showToast pattern */
    const newsletterForm = document.querySelector('.newsletter-form');
    const newsletterInput = document.querySelector('.newsletter-input');
    const newsletterBtn = document.querySelector('.newsletter-btn');
    // Safe toast helper to avoid ReferenceError if showToast isn't defined yet
    const safeToast = function (message, type) {
        try {
            const fn = (window.showToast || (window.templateGallery && window.templateGallery.showToast));
            if (typeof fn === 'function') fn(message, type);
        } catch (_) { }
    }
    if (newsletterForm && newsletterInput && newsletterBtn) {
        // Avoid unintended form submission by ensuring the button is not a submit button
        try { newsletterBtn.setAttribute('type', 'button'); } catch (_) { }
        const handleSubscribe = async function (e) {
            if (e) e.preventDefault();
            const email = newsletterInput.value ? newsletterInput.value.trim() : '';
            if (!email) { safeToast('Please enter your email', 'error'); return; }
            const original = newsletterBtn.innerHTML;
            newsletterBtn.disabled = true;
            newsletterBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
            try {
                // Backend is mounted at '/admin', route is '/subscribe'
                const resp = await fetch('/admin/subscribe?from=home-footer', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: email })
                });
                const data = await resp.json().catch(() => ({}));
                // Controller returns {choices:[{message:{content}}], status: 200|400} with HTTP 200
                const msg = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
                if (data && Number(data.status) === 200) {
                    safeToast(msg || 'Please check your email to verify your subscription.', 'success');
                    newsletterInput.value = '';
                } else {
                    safeToast(msg || 'Subscribe failed.', 'error');
                }
            } catch (err) {
                safeToast('Network error. Please try again.', 'error');
            } finally {
                newsletterBtn.disabled = false;
                newsletterBtn.innerHTML = original;
            }
        };
        // Bind on form submit to reliably catch Enter and button clicks
        newsletterForm.addEventListener('submit', handleSubscribe);
        // Click button
        newsletterBtn.addEventListener('click', handleSubscribe);
        // Press Enter in input (keydown is more reliable than keypress)
        newsletterInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubscribe(e);
            }
        });
    }
    /** Newsletter subscribe end */
});


// Toast notification system
window.showToast = function showToast(message, type = 'info') {
    // Remove existing toast
    const existingToast = document.querySelector('.toast-notification');
    if (existingToast) {
        existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = `toast-notification toast-${type}`;
    toast.innerHTML = `
          <div class="toast-content">
              <i class="fas fa-${type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle'} me-2"></i>
              <span>${message}</span>
          </div>
          <button class="toast-close" onclick="this.parentElement.remove()">
              <i class="fas fa-times"></i>
          </button>
      `;

    // Add toast styles
    const toastStyles = `
          .toast-notification {
              position: fixed;
              top: 20px;
              right: 20px;
              background: white;
              border-radius: 8px;
              padding: 16px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
              z-index: 10000;
              display: flex;
              align-items: center;
              justify-content: space-between;
              min-width: 300px;
              animation: slideInRight 0.3s ease;
              border-left: 4px solid #667eea;
          }
          .toast-success { border-left-color: #22c55e; }
          .toast-error { border-left-color: #ef4444; }
          .toast-content {
              display: flex;
              align-items: center;
              color: #374151;
              font-weight: 500;
          }
          .toast-close {
              background: none;
              border: none;
              color: #9ca3af;
              cursor: pointer;
              padding: 4px;
              margin-left: 12px;
          }
          .toast-close:hover {
              color: #374151;
          }
          @keyframes slideInRight {
              from {
                  opacity: 0;
                  transform: translateX(100%);
              }
              to {
                  opacity: 1;
                  transform: translateX(0);
              }
          }
      `;

    // Add styles if not already added
    if (!document.querySelector('#toast-styles')) {
        const styleElement = document.createElement('style');
        styleElement.id = 'toast-styles';
        styleElement.textContent = toastStyles;
        document.head.appendChild(styleElement);
    }

    document.body.appendChild(toast);

    // Auto-remove after 3 seconds
    setTimeout(() => {
        if (toast.parentElement) {
            toast.style.animation = 'slideInRight 0.3s ease reverse';
            setTimeout(() => toast.remove(), 300);
        }
    }, 3000);
}

// FAQ Handlers
function initializeFAQ() {
    var questions = document.querySelectorAll('.sb-faq li .sb-question');
    for (var i = 0; i < questions.length; i++) {
        (function (question) {
            question.addEventListener('click', function () {
                var toggle = question.querySelector('.sb-plus-minus-toggle');
                if (toggle) toggleClass(toggle, 'sb-collapsed');
                if (question.parentElement) toggleClass(question.parentElement, 'sb-active');
            });
        })(questions[i]);
    }
}
// Contact Form Handler
function initializeContactForm(id, postUrl) {
    var form = document.getElementById(id);
    if (!form) return;

    form.addEventListener('submit', function (e) {
        e.preventDefault(); // Prevent default form submission

        var submitBtn = form.querySelector('[type="submit"]');
        if (!submitBtn) return;

        var originalBtnText = submitBtn.innerHTML;
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Sending...';

        // Create XHR object for older browsers
        var xhr = new XMLHttpRequest();
        var formData = new FormData(form);

        xhr.open('POST', postUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.onreadystatechange = function () {
            // Reset button state
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalBtnText;

            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    // Show success message
                    var successElement = document.querySelector('.sb-success-result');
                    if (successElement) {
                        var textElement = successElement.querySelector('.sb-text');
                        if (textElement) {
                            textElement.textContent = JSON.parse(xhr.responseText).message;
                        }
                        addClass(successElement, 'sb-active');
                        // Hide success message after 5 seconds
                        setTimeout(function () {
                            removeClass(successElement, 'sb-active');
                        }, 5000);
                    }
                    form.reset(); // Reset form fields
                } else {
                    // Handle error
                    var errorElement = document.querySelector('.sb-error-result');
                    if (errorElement) {
                        var textElement = errorElement.querySelector('.sb-text');
                        if (textElement) {
                            textElement.textContent = 'Failed to send message. Please try again later.';
                        }
                        addClass(errorElement, 'sb-active');
                        setTimeout(function () {
                            removeClass(errorElement, 'sb-active');
                        }, 5000);
                    }
                }
            }
        };

        xhr.onerror = function () {
            // Reset button state
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalBtnText;

            // Show error message
            var errorElement = document.querySelector('.sb-error-result');
            if (errorElement) {
                var textElement = errorElement.querySelector('.sb-text');
                if (textElement) {
                    textElement.textContent = 'Network error. Please try again later.';
                }
                addClass(errorElement, 'sb-active');
                setTimeout(function () {
                    removeClass(errorElement, 'sb-active');
                }, 5000);
            }
        };

        // Standard ES5 manual form data serialization to object
        var dataObj = {};
        formData.forEach(function (val, key) {
            dataObj[key] = val;
        });
        xhr.send(JSON.stringify(dataObj));
    });
}

// Utility function to remove active class from elements
function removeActiveClass(selectors) {
    var elements = document.querySelectorAll(selectors);
    for (var i = 0; i < elements.length; i++) {
        removeClass(elements[i], 'sb-active');
    }
}
